import { Box, Typography, Checkbox, Divider } from "@mui/joy";
import { useFormContext } from "react-hook-form";

import { usePermissions } from "../../../../../hooks/query";
import { TextField, Textarea, Button } from "../../../../../shared";

/**
 * Groups permissions by their prefix (e.g., "Supplier", "Role", "User")
 */
function groupPermissions(permissions: any[]) {
    const groups: Record<string, any[]> = {};

    permissions.forEach((perm) => {
        let groupName = "General";
        const name = perm.Name || perm.Id;

        if (name.includes(":")) {
            groupName = name.split(":")[0].trim();
        } else if (name.includes(".")) {
            groupName = name.split(".")[0].trim();
            groupName = groupName.charAt(0).toUpperCase() + groupName.slice(1);
        } else {
            const keywords = ["Supplier", "Role", "User", "Project", "Lead", "Inventory", "Permission"];
            const found = keywords.find((k) => name.toLowerCase().includes(k.toLowerCase()));
            if (found) groupName = found;
        }

        if (!groups[groupName]) groups[groupName] = [];
        groups[groupName].push(perm);
    });

    return groups;
}

function permissionLabel(perm: any) {
    const name = String(perm.Name || perm.Id);
    if (name.includes(":")) return name.split(":").slice(-1)[0].trim();
    if (name.includes(".")) return name.split(".").slice(-1)[0].trim();
    return name;
}

export default function RoleConfiguration({ readOnly = false }: { readOnly?: boolean }) {
    const { useList } = usePermissions();
    const listPermissionsSvc = useList();
    const permissions = listPermissionsSvc.data || [];
    const groupedPermissions = groupPermissions(permissions);

    const { watch, setValue } = useFormContext();
    const selectedPermissions = watch("Permissions") || [];

    const handleTogglePermission = (permId: string) => {
        if (readOnly) return;
        const isSelected = selectedPermissions.some((p: any) => p.Id === permId);
        if (isSelected) {
            setValue(
                "Permissions",
                selectedPermissions.filter((p: any) => p.Id !== permId),
                { shouldDirty: true },
            );
            return;
        }

        const perm = permissions.find((p: any) => p.Id === permId);
        if (perm) {
            setValue("Permissions", [...selectedPermissions, perm], { shouldDirty: true });
        }
    };

    const handleSelectAll = (isSelect: boolean) => {
        if (readOnly) return;
        setValue("Permissions", isSelect ? permissions : [], { shouldDirty: true });
    };

    const handleToggleGroup = (groupPerms: any[], isSelect: boolean) => {
        if (readOnly) return;
        const otherPermissions = selectedPermissions.filter(
            (sp: any) => !groupPerms.some((gp) => gp.Id === sp.Id),
        );

        setValue(
            "Permissions",
            isSelect ? [...otherPermissions, ...groupPerms] : otherPermissions,
            { shouldDirty: true },
        );
    };

    const isAllSelected = selectedPermissions.length === permissions.length && permissions.length > 0;
    const groupEntries = Object.entries(groupedPermissions).sort(([a], [b]) => a.localeCompare(b));

    return (
        <Box flex={1}>
            <Box mt={0} display="flex" flexDirection="column" gap={1.5}>
                <TextField
                    name="Name"
                    label="Role Name"
                    helperText="Descriptive name (e.g. Project Manager)"
                    placeholder="e.g., Project Manager"
                    disabled={readOnly}
                />
                <Textarea
                    name="Description"
                    label="Description"
                    helperText="Briefly describe role responsibilities"
                    placeholder="e.g., Handles project scheduling and team coordination"
                    minRows={3}
                    disabled={readOnly}
                />

                <Box mt={1}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        gap={2}
                        mb={1}
                    >
                        <Box>
                            <Typography level="title-sm">Permissions</Typography>
                            <Typography level="body-xs" sx={{ color: "text.secondary", mt: 0.25 }}>
                                {selectedPermissions.length} of {permissions.length} selected
                            </Typography>
                        </Box>
                        {!readOnly ? (
                            <Button
                                type="button"
                                variant="plain"
                                color="neutral"
                                size="sm"
                                onClick={() => handleSelectAll(!isAllSelected)}
                                disabled={!permissions.length}
                                sx={{ borderRadius: "4px", flexShrink: 0 }}
                            >
                                {isAllSelected ? "Clear all" : "Select all"}
                            </Button>
                        ) : null}
                    </Box>

                    <Divider sx={{ mb: 1.5 }} />

                    {listPermissionsSvc.isLoading ? (
                        <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                            Loading permissions…
                        </Typography>
                    ) : !permissions.length ? (
                        <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                            No permissions available.
                        </Typography>
                    ) : (
                        <Box display="flex" flexDirection="column" gap={2}>
                            {groupEntries.map(([group, perms], groupIndex) => {
                                const groupSelectedCount = perms.filter((p) =>
                                    selectedPermissions.some((sp: any) => sp.Id === p.Id),
                                ).length;
                                const isGroupFull = groupSelectedCount === perms.length;

                                return (
                                    <Box key={group}>
                                        {groupIndex > 0 ? <Divider sx={{ mb: 2 }} /> : null}

                                        <Box
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            mb={0.5}
                                            gap={1}
                                        >
                                            <Typography
                                                level="body-xs"
                                                sx={{
                                                    fontWeight: 600,
                                                    letterSpacing: "0.06em",
                                                    textTransform: "uppercase",
                                                    color: "text.tertiary",
                                                }}
                                            >
                                                {group}{" "}
                                                <Typography
                                                    component="span"
                                                    level="body-xs"
                                                    sx={{
                                                        letterSpacing: 0,
                                                        textTransform: "none",
                                                        fontWeight: 500,
                                                        color: "text.secondary",
                                                    }}
                                                >
                                                    ({groupSelectedCount}/{perms.length})
                                                </Typography>
                                            </Typography>
                                            {!readOnly ? (
                                                <Button
                                                    type="button"
                                                    variant="plain"
                                                    color="neutral"
                                                    size="sm"
                                                    onClick={() => handleToggleGroup(perms, !isGroupFull)}
                                                    sx={{ borderRadius: "4px", minHeight: 28, px: 1 }}
                                                >
                                                    {isGroupFull ? "Clear" : "Select group"}
                                                </Button>
                                            ) : null}
                                        </Box>

                                        <Box
                                            display="grid"
                                            gridTemplateColumns={{
                                                xs: "1fr",
                                                sm: "1fr 1fr",
                                            }}
                                            columnGap={2}
                                            rowGap={0.25}
                                        >
                                            {perms.map((perm) => {
                                                const isSelected = selectedPermissions.some(
                                                    (p: any) => p.Id === perm.Id,
                                                );
                                                return (
                                                    <Box
                                                        key={perm.Id}
                                                        component="label"
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "flex-start",
                                                            gap: 1,
                                                            py: 0.75,
                                                            cursor: readOnly ? "default" : "pointer",
                                                        }}
                                                    >
                                                        <Checkbox
                                                            size="sm"
                                                            checked={isSelected}
                                                            disabled={readOnly}
                                                            onChange={() =>
                                                                handleTogglePermission(perm.Id)
                                                            }
                                                            sx={{ mt: 0.15 }}
                                                        />
                                                        <Box minWidth={0}>
                                                            <Typography
                                                                level="body-sm"
                                                                sx={{ fontWeight: 500 }}
                                                            >
                                                                {permissionLabel(perm)}
                                                            </Typography>
                                                            {perm.Description ? (
                                                                <Typography
                                                                    level="body-xs"
                                                                    sx={{
                                                                        color: "text.tertiary",
                                                                        mt: 0.15,
                                                                    }}
                                                                >
                                                                    {perm.Description}
                                                                </Typography>
                                                            ) : null}
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
